import React, { useState } from "react";
import { Row, Col, Button, Modal, Spin } from "antd";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  createSubscription,
  manageSubscription,
  handleFreePlanSubscription,
} from "@api/subscription/stripe";
import {
  updateSubscription,
  cancelSubscription,
  previewSubscriptionChange,
  getPlanAction,
  getTierFromPlanName,
  isFreePlan as checkIsFreePlan,
} from "@api/subscription/subscriptionManagement";
import { getLastActionOfSelectUser } from "@api/user/appsumo";
import { InfoNotification, ErrorNotification, SuccessNotification } from "@components";
import { useTranslation } from "react-i18next";
import { getAdminSubscriptionAction } from "@redux/actions/adminActions";
import {
  FiCheck,
  FiArrowUp,
  FiArrowDown,
  FiRefreshCw,
  FiCalendar,
  FiAlertCircle,
  FiZap,
  FiStar,
  FiAward,
  FiTrendingUp,
  FiCreditCard,
  FiExternalLink,
} from "react-icons/fi";

// Plan icons mapping
const planIcons = {
  Free: FiZap,
  Optimizer: FiStar,
  Wizard: FiAward,
  Maverick: FiTrendingUp,
};

// Plan gradient colors
const planGradients = {
  Free: { from: "#64748b", to: "#475569" },
  Optimizer: { from: "#3b82f6", to: "#1d4ed8" },
  Wizard: { from: "#8b5cf6", to: "#6d28d9" },
  Maverick: { from: "#f59e0b", to: "#d97706" },
};

const PricingItem = (props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const { t } = useTranslation();

  // Get current subscription info
  const currentPlan = props.currentSubscription?.currentPlan;
  const currentTier = props.currentSubscription?.tier ?? getTierFromPlanName(currentPlan);
  const currentInterval = props.currentSubscription?.subscription?.billInterval;
  const isCancelScheduled = props.currentSubscription?.subscriptionStatus === "canceled" ||
    props.currentSubscription?.subscription?.cancelAtPeriodEnd;
  const isCurrentPlanFree = currentTier === 0 || currentPlan?.toLowerCase() === "free";

  // Use billingInterval prop if available, otherwise fall back to annualCycle
  const selectedInterval = props.billingInterval || (props.annualCycle ? "year" : "month");

  /**
   * Get the price for the current billing interval
   */
  const getPriceForInterval = (item) => {
    switch (selectedInterval) {
      case "week":
        return item.weeklyPrice ?? Math.round((item.monthlyPrice ?? 0) / 4);
      case "month":
        return item.monthlyPrice ?? 0;
      case "year":
        return item.annualPrice ?? 0;
      default:
        return item.monthlyPrice ?? 0;
    }
  };

  /**
   * Get the Stripe price IDs for the current billing interval
   */
  const getStripePriceIds = (item) => {
    switch (selectedInterval) {
      case "week":
        return {
          basePricingId: item.stripeWeeklyPriceId,
          meteredPricingId: item.stripeWeeklyMeteredPriceId,
        };
      case "month":
        return {
          basePricingId: item.stripeMonthlyPriceId,
          meteredPricingId: item.stripeMonthlyMeteredPriceId,
        };
      case "year":
        return {
          basePricingId: item.stripeAnnualPriceId,
          meteredPricingId: item.stripeAnnualMeteredPriceId,
        };
      default:
        return {
          basePricingId: item.stripeMonthlyPriceId,
          meteredPricingId: item.stripeMonthlyMeteredPriceId,
        };
    }
  };

  /**
   * Refresh subscription data
   */
  const refreshSubscription = () => {
    dispatch(getAdminSubscriptionAction(() => {}));
  };

  /**
   * Format currency amount (assumes cents)
   */
  const formatCurrency = (amount, currency = "usd") => {
    if (!amount && amount !== 0) return "$0.00";
    const formatted = (Math.abs(amount) / 100).toFixed(2);
    const symbols = { usd: "$", eur: "EUR", gbp: "GBP", inr: "INR" };
    const prefix = amount < 0 ? "-" : "";
    return `${prefix}${symbols[currency] || "$"}${formatted}`;
  };

  /**
   * Get interval label for display
   */
  const getIntervalLabel = (interval) => {
    switch (interval) {
      case "week":
        return t("profile.subscription.weekly") || "Weekly";
      case "month":
        return t("profile.subscription.monthly") || "Monthly";
      case "year":
        return t("profile.subscription.annual") || "Annual";
      default:
        return interval;
    }
  };

  /**
   * Handle AppSumo redirect
   */
  const handleAppSumoRedirect = async () => {
    try {
      const res = await getLastActionOfSelectUser(props.user.email);
      window.open(
        `https://appsumo.com/account/redemption/${res.data.lastAction.invoice_item_uuid}#change-plan`,
        "_blank"
      );
    } catch (err) {
      console.error(err);
      ErrorNotification("Failed to redirect to AppSumo");
    }
    setLoading(false);
    setLoadingPlanId(null);
  };

  /**
   * Handle new subscription (for Free users upgrading)
   */
  const handleNewSubscription = async (e, item) => {
    e.preventDefault();

    if (props.user?.appSumoUser) {
      setLoading(true);
      await handleAppSumoRedirect();
      return;
    }

    const { basePricingId, meteredPricingId } = getStripePriceIds(item);

    if (!basePricingId) {
      ErrorNotification(t("profile.subscription.priceNotAvailable") || "This plan is not available for the selected billing interval.");
      return;
    }

    setLoading(true);
    setLoadingPlanId(item._id);

    try {
      const res = await createSubscription({ basePricingId, meteredPricingId });
      window.location.href = res.data.checkoutUrl;
    } catch (err) {
      setLoading(false);
      setLoadingPlanId(null);
      ErrorNotification(t("profile.subscription.checkoutError") || "Failed to start checkout. Please try again.");
    }
  };

  /**
   * Handle subscription update (upgrade/downgrade/switch interval)
   */
  const handleUpdateSubscription = async (item, action) => {
    if (props.user?.appSumoUser) {
      setLoading(true);
      await handleAppSumoRedirect();
      return;
    }

    setPreviewLoading(true);
    setLoadingPlanId(item._id);

    try {
      // Get proration preview
      const previewRes = await previewSubscriptionChange(item._id, selectedInterval);

      if (previewRes.data?.success && previewRes.data?.preview) {
        // Extract data from server response format
        const preview = previewRes.data.preview;
        setPreviewData({
          planName: item.name,
          planId: item._id,
          action,
          // Server response fields
          changeType: preview.changeType,
          currentPlan: preview.currentPlan,
          currentInterval: preview.currentInterval,
          newPlan: preview.newPlan,
          newInterval: preview.newInterval,
          // Amounts (in cents from Stripe)
          amountDue: preview.amountDue,
          subtotal: preview.subtotal,
          total: preview.total,
          currency: preview.currency,
          // Account credit & net amount
          accountCredit: preview.accountCredit || 0,
          creditApplied: preview.creditApplied || 0,
          netAmount: preview.netAmount ?? preview.amountDue,
          // Formatted amounts (already in dollars)
          amountDueFormatted: preview.amountDueFormatted,
          totalFormatted: preview.totalFormatted,
          accountCreditFormatted: preview.accountCreditFormatted || "0.00",
          creditAppliedFormatted: preview.creditAppliedFormatted || "0.00",
          netAmountFormatted: preview.netAmountFormatted || preview.amountDueFormatted,
          // Line items for detailed breakdown
          lineItems: preview.lineItems || [],
          // Period info
          periodStart: preview.periodStart,
          periodEnd: preview.periodEnd,
          // Checkout redirect (for upgrades) - Netflix/Spotify style
          requiresCheckout: preview.requiresCheckout || false,
          checkoutUrl: preview.checkoutUrl,
          newPlanPrice: preview.newPlanPrice,
          newPlanPriceFormatted: preview.newPlanPriceFormatted,
        });
      } else {
        // If preview fails, proceed without preview (fallback to direct update)
        await executeSubscriptionUpdate(item, action);
      }
    } catch (err) {
      // If preview endpoint doesn't exist or fails, proceed without preview
      console.log("Preview not available, proceeding directly:", err.message);
      await executeSubscriptionUpdate(item, action);
    } finally {
      setPreviewLoading(false);
      setLoadingPlanId(null);
    }
  };

  /**
   * Execute the actual subscription update
   */
  const executeSubscriptionUpdate = async (item, action) => {
    setLoading(true);
    setLoadingPlanId(item?._id || previewData?.planId);

    try {
      const res = await updateSubscription(item?._id || previewData?.planId, selectedInterval);

      if (res.data?.success) {
        // For upgrades requiring checkout, redirect to Stripe Checkout
        if (res.data?.requiresCheckout && res.data?.checkoutUrl) {
          window.location.href = res.data.checkoutUrl;
          return;
        }

        // For upgrades requiring 3DS authentication, redirect to hosted invoice
        if (res.data?.requiresAction && res.data?.hostedInvoiceUrl) {
          InfoNotification(res.data.message || "Redirecting to complete payment...");
          window.location.href = res.data.hostedInvoiceUrl;
          return;
        }

        const actionText = action === "switch-interval"
          ? t("profile.subscription.intervalSwitched") || "Billing interval updated successfully!"
          : action === "upgrade"
          ? t("profile.subscription.upgraded") || "Plan upgraded successfully!"
          : t("profile.subscription.downgraded") || "Plan changed successfully!";

        SuccessNotification(actionText);
        refreshSubscription();
      } else {
        ErrorNotification(res.data?.message || t("profile.subscription.updateError") || "Failed to update subscription");
      }
    } catch (err) {
      console.error("Update subscription error:", err);

      // Check if error response contains hostedInvoiceUrl (3DS required)
      if (err.response?.data?.hostedInvoiceUrl) {
        InfoNotification("Redirecting to complete payment...");
        window.location.href = err.response.data.hostedInvoiceUrl;
        return;
      }

      ErrorNotification(err.response?.data?.message || err.response?.data?.type?.[0]?.message || t("profile.subscription.updateError") || "Failed to update subscription. Please try again.");
    } finally {
      setLoading(false);
      setLoadingPlanId(null);
      setPreviewData(null);
    }
  };

  /**
   * Handle downgrade to Free (cancel subscription at period end)
   */
  const handleDowngradeToFree = async (e) => {
    e.preventDefault();

    if (props.user?.appSumoUser) {
      setLoading(true);
      await handleAppSumoRedirect();
      return;
    }

    Modal.confirm({
      title: t("profile.subscription.confirmDowngrade") || "Downgrade to Free Plan?",
      content: (
        <div style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 8 }}>
            {t("profile.subscription.downgradeWarning") ||
              "Your subscription will remain active until the end of your current billing period. After that, you'll be switched to the Free plan."}
          </p>
          {props.currentSubscription?.subscription?.endPeriod && (
            <p style={{ color: "#f59e0b", fontWeight: 500 }}>
              <FiCalendar style={{ marginRight: 4, verticalAlign: "middle" }} />
              {t("profile.subscription.accessUntil") || "You'll have access until:"} {
                new Date(props.currentSubscription.subscription.endPeriod * 1000).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              }
            </p>
          )}
        </div>
      ),
      okText: t("profile.subscription.confirmDowngradeBtn") || "Yes, Downgrade",
      okType: "danger",
      cancelText: t("common.cancel") || "Cancel",
      onOk: async () => {
        setLoading(true);
        try {
          const res = await cancelSubscription();

          if (res.data?.success) {
            SuccessNotification(
              res.data.message ||
              t("profile.subscription.downgradeScheduled") ||
              "Your subscription will be canceled at the end of the billing period."
            );
            refreshSubscription();
          } else {
            ErrorNotification(res.data?.message || t("profile.subscription.cancelError") || "Failed to schedule downgrade");
          }
        } catch (err) {
          console.error("Cancel subscription error:", err);
          // Fallback to free plan subscription handler
          try {
            await handleFreePlanSubscription();
            InfoNotification(t("profile.subscription.downgraded") || "Subscription downgraded");
            window.location.reload();
          } catch (fallbackErr) {
            ErrorNotification(t("profile.subscription.cancelError") || "Failed to schedule downgrade. Please try again.");
          }
        } finally {
          setLoading(false);
        }
      },
    });
  };

  /**
   * Handle manage billing (open Stripe portal)
   */
  const handleManageBilling = async (e) => {
    e.preventDefault();

    if (props.user?.appSumoUser) {
      setLoading(true);
      await handleAppSumoRedirect();
      return;
    }

    setLoading(true);
    try {
      const res = await manageSubscription();
      window.location.href = res.data.portalUrl;
    } catch (err) {
      setLoading(false);
      console.error(err);
      ErrorNotification(t("profile.subscription.portalError") || "Failed to open billing portal");
    }
  };

  /**
   * Get button text based on action
   */
  const getButtonText = (item, action) => {
    if (loadingPlanId === item._id) {
      return <Spin size="small" />;
    }

    switch (action) {
      case "current":
        return (
          <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <FiCheck /> {t("profile.subscription.currentPlan") || "Current Plan"}
          </span>
        );
      case "switch-interval":
        return (
          <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <FiRefreshCw /> {t("profile.subscription.switchTo") || "Switch to"} {getIntervalLabel(selectedInterval)}
          </span>
        );
      case "upgrade":
        return (
          <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <FiArrowUp /> {t("profile.subscription.upgrade") || "Upgrade"}
          </span>
        );
      case "downgrade":
        return (
          <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <FiArrowDown /> {t("profile.subscription.downgrade") || "Downgrade"}
          </span>
        );
      case "downgrade-free":
        if (isCancelScheduled) {
          return (
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <FiCalendar /> {t("profile.subscription.scheduled") || "Scheduled"}
            </span>
          );
        }
        return (
          <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <FiArrowDown /> {t("profile.subscription.downgradeToFree") || "Downgrade to Free"}
          </span>
        );
      default:
        return t("profile.subscription.subscribe") || "Subscribe";
    }
  };

  /**
   * Handle button click based on action
   */
  const handleButtonClick = (e, item, action) => {
    e.preventDefault();

    switch (action) {
      case "current":
        // Do nothing for current plan
        break;
      case "switch-interval":
      case "upgrade":
      case "downgrade":
        if (isCurrentPlanFree && action === "upgrade") {
          // Free users need to go through checkout
          handleNewSubscription(e, item);
        } else {
          // Existing subscribers can update directly
          handleUpdateSubscription(item, action);
        }
        break;
      case "downgrade-free":
        if (!isCancelScheduled) {
          handleDowngradeToFree(e);
        }
        break;
      default:
        handleNewSubscription(e, item);
    }
  };

  const listMap = Array.isArray(props.plans) ? props.plans.map((item, index) => {
    // Determine plan action
    const planAction = getPlanAction(
      currentPlan,
      currentTier,
      currentInterval,
      item,
      selectedInterval
    );

    const isFree = checkIsFreePlan(item);
    const isDisabled = loading ||
      planAction === "current" ||
      (planAction === "downgrade-free" && isCancelScheduled) ||
      !props.billingAdmin;
    const isCurrent = planAction === "current";
    const isHovered = hoveredCard === index;
    const isPopular = item.name === "Wizard" || item.isPopular; // Wizard is most popular

    // Get price for current interval
    const displayPrice = getPriceForInterval(item);
    const PlanIcon = planIcons[item.name] || FiStar;
    const gradient = planGradients[item.name] || planGradients.Optimizer;

    // Card styles
    const cardStyle = {
      position: "relative",
      padding: "28px 24px",
      background: isCurrent
        ? `linear-gradient(135deg, ${gradient.from}08 0%, ${gradient.to}12 100%)`
        : "#fff",
      borderRadius: "20px",
      border: isCurrent
        ? `2px solid ${gradient.from}`
        : isHovered
          ? "2px solid #e2e8f0"
          : "1px solid #e2e8f0",
      boxShadow: isCurrent
        ? `0 8px 32px ${gradient.from}25, 0 0 0 4px ${gradient.from}10`
        : isHovered
          ? "0 12px 40px rgba(0, 0, 0, 0.12)"
          : "0 2px 8px rgba(0, 0, 0, 0.04)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      transform: isHovered && !isCurrent ? "translateY(-4px)" : "translateY(0)",
      display: "flex",
      flexDirection: "column",
      minHeight: "520px",
      overflow: "hidden",
    };

    // Badge styles
    const badgeStyle = {
      position: "absolute",
      top: "-1px",
      left: "50%",
      transform: "translateX(-50%)",
      background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
      color: "#fff",
      padding: "6px 16px",
      borderRadius: "0 0 12px 12px",
      fontSize: "11px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      boxShadow: `0 4px 12px ${gradient.from}40`,
    };

    // Popular badge (different style)
    const popularBadgeStyle = {
      position: "absolute",
      top: "16px",
      right: "-32px",
      background: "linear-gradient(135deg, #f59e0b, #d97706)",
      color: "#fff",
      padding: "4px 40px",
      fontSize: "10px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      transform: "rotate(45deg)",
      boxShadow: "0 2px 8px rgba(245, 158, 11, 0.4)",
    };

    // Icon container styles
    const iconContainerStyle = {
      width: "56px",
      height: "56px",
      borderRadius: "16px",
      background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "16px",
      boxShadow: `0 4px 16px ${gradient.from}30`,
    };

    // Price styles
    const priceStyle = {
      fontSize: "42px",
      fontWeight: 800,
      color: "#0f172a",
      lineHeight: 1,
      marginBottom: "4px",
      letterSpacing: "-1px",
    };

    // Button styles based on action
    const getButtonStyle = () => {
      const baseStyle = {
        height: "48px",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        transition: "all 0.2s ease",
        marginTop: "auto",
        border: "none",
      };

      if (planAction === "current") {
        return {
          ...baseStyle,
          background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
          color: "#fff",
          cursor: "default",
          opacity: 0.9,
        };
      }
      if (planAction === "upgrade") {
        return {
          ...baseStyle,
          background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
          color: "#fff",
          boxShadow: `0 4px 16px ${gradient.from}40`,
        };
      }
      if (planAction === "switch-interval") {
        return {
          ...baseStyle,
          background: `${gradient.from}15`,
          color: gradient.from,
          border: `1px solid ${gradient.from}30`,
        };
      }
      return {
        ...baseStyle,
        background: "#f8fafc",
        color: "#64748b",
        border: "1px solid #e2e8f0",
      };
    };

    // Feature check icon
    const FeatureCheck = () => (
      <div style={{
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        background: `${gradient.from}15`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}>
        <FiCheck size={12} color={gradient.from} strokeWidth={3} />
      </div>
    );

    return (
      <Col
        xs={24}
        sm={12}
        lg={6}
        key={index}
        style={{ marginBottom: "24px", padding: "0 12px" }}
      >
        <div
          style={cardStyle}
          onMouseEnter={() => setHoveredCard(index)}
          onMouseLeave={() => setHoveredCard(null)}
        >
          {/* Current Plan Badge */}
          {isCurrent && (
            <div style={badgeStyle}>
              {t("profile.subscription.yourPlan") || "Your Plan"}
            </div>
          )}

          {/* Popular Badge */}
          {isPopular && !isCurrent && (
            <div style={popularBadgeStyle}>
              {t("profile.subscription.popular") || "Popular"}
            </div>
          )}

          {/* Plan Icon */}
          <div style={iconContainerStyle}>
            <PlanIcon size={28} color="#fff" />
          </div>

          {/* Plan Name */}
          <h3 style={{
            fontSize: "22px",
            fontWeight: 700,
            color: "#0f172a",
            margin: "0 0 8px 0",
          }}>
            {item.name}
          </h3>

          {/* Meta Description */}
          {item?.metaDescription && (
            <p style={{
              fontSize: "13px",
              color: gradient.from,
              fontWeight: 500,
              margin: "0 0 16px 0",
              lineHeight: 1.4,
            }}>
              {item.metaDescription}
            </p>
          )}

          {/* Price Section */}
          {item?.name === "Enterprise" ? (
            <div style={{ marginBottom: "20px" }}>
              <Button
                type="primary"
                style={{
                  background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
                  border: "none",
                  borderRadius: "8px",
                  height: "36px",
                  fontWeight: 600,
                }}
              >
                <a
                  href="https://www.netraga.com/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#fff" }}
                >
                  {t("profile.subscription.contactUs") || "Contact Us"}
                </a>
              </Button>
            </div>
          ) : (
            <div style={{ marginBottom: "20px" }}>
              {!props.user?.appSumoUser ? (
                <>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                    <span style={priceStyle}>
                      ${(displayPrice / 100).toFixed(0)}
                    </span>
                    <span style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#64748b",
                    }}>
                      /{selectedInterval === "week" ? "wk" : selectedInterval === "year" ? "yr" : "mo"}
                    </span>
                  </div>
                  {selectedInterval === "year" && (item.monthlyPrice ?? 0) > 0 && (
                    <p style={{
                      fontSize: "12px",
                      color: "#94a3b8",
                      margin: "4px 0 0 0",
                    }}>
                      ${((item.monthlyPrice || 0) / 100).toFixed(0)}/mo billed monthly
                    </p>
                  )}
                </>
              ) : (
                <div>
                  {props.user?.appSumoUser && props.user?.currentPlan === item.name ? (
                    <>
                      <del style={{ color: "#94a3b8", fontSize: "18px" }}>
                        {item.name === "Optimizer" ? "$49" : item.name === "Wizard" ? "$99" : item.name === "Free" ? "$0" : "$199"}
                      </del>
                      <p style={{
                        color: "#10b981",
                        fontSize: "20px",
                        fontWeight: 700,
                        margin: "4px 0 0 0",
                      }}>
                        {t("profile.subscription.lifetime") || "Lifetime"}
                      </p>
                    </>
                  ) : (
                    <span style={priceStyle}>
                      {item.name === "Optimizer" ? "$49" : item.name === "Wizard" ? "$99" : item.name === "Free" ? "$0" : "$199"}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Billing text */}
          {!props.user?.appSumoUser && (
            <p style={{
              fontSize: "12px",
              color: "#94a3b8",
              margin: "0 0 20px 0",
            }}>
              {props.billedText}
            </p>
          )}

          {/* Features List */}
          <div style={{
            flex: 1,
            marginBottom: "24px",
          }}>
            {(item.features || []).map((feature, featureIndex) => {
              let featureName = '';
              if (typeof feature === 'string') {
                featureName = feature;
              } else if (typeof feature === 'object' && feature !== null) {
                if (feature.name) {
                  featureName = feature.name;
                } else {
                  const chars = [];
                  let i = 0;
                  while (feature[i] !== undefined) {
                    chars.push(feature[i]);
                    i++;
                  }
                  featureName = chars.join('');
                }
              }
              if (!featureName) return null;
              return (
                <div
                  key={featureIndex}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    marginBottom: "12px",
                  }}
                >
                  <FeatureCheck />
                  <span style={{
                    fontSize: "13px",
                    color: "#475569",
                    lineHeight: 1.5,
                  }}>
                    {featureName}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Action Button */}
          <Button
            block
            onClick={(e) => handleButtonClick(e, item, planAction)}
            loading={loadingPlanId === item._id && loading}
            disabled={isDisabled}
            style={getButtonStyle()}
          >
            {getButtonText(item, planAction)}
          </Button>

          {/* Cancel scheduled note */}
          {planAction === "downgrade-free" && isCancelScheduled && (
            <div style={{
              marginTop: "12px",
              padding: "10px 12px",
              background: "#fef3c7",
              borderRadius: "8px",
              fontSize: "12px",
              color: "#92400e",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
            }}>
              <FiCalendar size={14} />
              {t("profile.subscription.switchesAfter") || "Switches to Free after"}{" "}
              {props.currentSubscription?.subscription?.endPeriod
                ? new Date(props.currentSubscription.subscription.endPeriod * 1000).toLocaleDateString()
                : "billing period ends"}
            </div>
          )}
        </div>
      </Col>
    );
  }) : null;

  // Proration preview modal - clean design matching extension
  // Handles both proration (downgrade/interval change) and checkout redirect (upgrade)
  const renderPreviewModal = () => {
    if (!previewData) return null;

    // Check if this is a checkout redirect flow (Netflix/Spotify style upgrade)
    const isCheckoutFlow = previewData.requiresCheckout === true;

    // Get amounts from preview data
    const chargeAmount = previewData.amountDue ?? 0;
    const creditApplied = previewData.creditApplied ?? 0;
    const netAmount = previewData.netAmount ?? chargeAmount;
    const hasCredit = creditApplied > 0;

    // Check if this results in a credit (downgrade scenario with no charge)
    const isCredit = chargeAmount < 0;
    const displayChargeAmount = Math.abs(chargeAmount / 100).toFixed(2);
    const displayCreditApplied = (creditApplied / 100).toFixed(2);
    const displayNetAmount = (netAmount / 100).toFixed(2);

    // For checkout flow, get the new plan price
    const newPlanPrice = previewData.newPlanPrice ?? 0;
    const displayNewPlanPrice = previewData.newPlanPriceFormatted || (newPlanPrice / 100).toFixed(2);

    // Get action label
    const actionLabel = previewData.action === "upgrade"
      ? "Upgrading to"
      : previewData.action === "downgrade"
      ? "Downgrading to"
      : "Switching to";

    // Get interval label
    const intervalLabel = selectedInterval === "week"
      ? "Weekly billing"
      : selectedInterval === "year"
      ? "Annual billing"
      : "Monthly billing";

    return (
      <Modal
        title={null}
        visible={!!previewData}
        onCancel={() => setPreviewData(null)}
        footer={null}
        centered
        width={420}
        bodyStyle={{ padding: 0 }}
        closable={true}
      >
        <div style={{ padding: "24px 28px" }}>
          {/* Header */}
          <h3 style={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#1e293b",
            margin: "0 0 24px 0",
            textAlign: "center",
          }}>
            {isCheckoutFlow ? "Complete Your Upgrade" : "Confirm Plan Change"}
          </h3>

          {/* Plan info - centered */}
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <p style={{
              fontSize: "14px",
              color: "#64748b",
              margin: "0 0 4px 0",
            }}>
              {actionLabel}
            </p>
            <h2 style={{
              fontSize: "28px",
              fontWeight: 700,
              color: "#8b5cf6",
              margin: "0 0 4px 0",
            }}>
              {previewData.planName || previewData.newPlan}
            </h2>
            <p style={{
              fontSize: "13px",
              color: "#94a3b8",
              margin: 0,
            }}>
              ({intervalLabel})
            </p>
          </div>

          {/* Amount box - different content for checkout vs proration */}
          <div style={{
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
            border: "1px solid #e2e8f0",
            borderRadius: "16px",
            padding: "24px",
            marginBottom: "24px",
          }}>
            {isCheckoutFlow ? (
              // CHECKOUT FLOW: Show new plan price and redirect message
              <div style={{ textAlign: "center" }}>
                <div style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px auto",
                }}>
                  <FiCreditCard size={24} color="#fff" />
                </div>
                <p style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: "0 0 8px 0",
                }}>
                  New plan price
                </p>
                <p style={{
                  fontSize: "36px",
                  fontWeight: 700,
                  color: "#1e293b",
                  margin: "0 0 4px 0",
                  letterSpacing: "-1px",
                }}>
                  ${displayNewPlanPrice}
                </p>
                <p style={{
                  fontSize: "13px",
                  color: "#94a3b8",
                  margin: "0 0 16px 0",
                }}>
                  per {selectedInterval === "week" ? "week" : selectedInterval === "year" ? "year" : "month"}
                </p>
                <div style={{
                  height: "1px",
                  background: "#e2e8f0",
                  margin: "0 0 16px 0",
                }} />
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  color: "#64748b",
                  fontSize: "13px",
                }}>
                  <FiExternalLink size={14} />
                  <span>You'll be redirected to secure checkout</span>
                </div>
                {hasCredit && (
                  <p style={{
                    fontSize: "13px",
                    color: "#10b981",
                    margin: "12px 0 0 0",
                  }}>
                    Your ${displayCreditApplied} account credit will be applied
                  </p>
                )}
              </div>
            ) : isCredit ? (
              // Downgrade credit scenario
              <div style={{ textAlign: "center" }}>
                <p style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: "0 0 8px 0",
                }}>
                  Account credit
                </p>
                <p style={{
                  fontSize: "36px",
                  fontWeight: 700,
                  color: "#10b981",
                  margin: "0 0 12px 0",
                  letterSpacing: "-1px",
                }}>
                  ${displayChargeAmount}
                </p>
                <div style={{
                  height: "1px",
                  background: "#e2e8f0",
                  margin: "0 0 12px 0",
                }} />
                <p style={{
                  fontSize: "13px",
                  color: "#94a3b8",
                  margin: 0,
                }}>
                  This credit will be applied to your next invoice
                </p>
              </div>
            ) : chargeAmount > 0 ? (
              // Upgrade/change with charge (proration)
              <div>
                {/* Charge row */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: hasCredit ? "12px" : "0",
                }}>
                  <span style={{
                    fontSize: "14px",
                    color: "#64748b",
                  }}>
                    Prorated charge
                  </span>
                  <span style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#1e293b",
                  }}>
                    ${displayChargeAmount}
                  </span>
                </div>

                {/* Credit row (if applicable) */}
                {hasCredit && (
                  <>
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "12px",
                    }}>
                      <span style={{
                        fontSize: "14px",
                        color: "#10b981",
                      }}>
                        Account credit
                      </span>
                      <span style={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#10b981",
                      }}>
                        -${displayCreditApplied}
                      </span>
                    </div>

                    {/* Divider */}
                    <div style={{
                      height: "1px",
                      background: "#e2e8f0",
                      margin: "0 0 12px 0",
                    }} />

                    {/* Net payment row */}
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}>
                      <span style={{
                        fontSize: "15px",
                        fontWeight: 600,
                        color: "#1e293b",
                      }}>
                        You pay now
                      </span>
                      <span style={{
                        fontSize: "24px",
                        fontWeight: 700,
                        color: netAmount === 0 ? "#10b981" : "#1e293b",
                      }}>
                        ${displayNetAmount}
                      </span>
                    </div>
                  </>
                )}

                {/* No credit - show single amount prominently */}
                {!hasCredit && (
                  <div style={{ textAlign: "center", marginTop: "8px" }}>
                    <p style={{
                      fontSize: "32px",
                      fontWeight: 700,
                      color: "#1e293b",
                      margin: "8px 0 0 0",
                      letterSpacing: "-1px",
                    }}>
                      ${displayChargeAmount}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // No charge scenario
              <div style={{ textAlign: "center" }}>
                <p style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: "0 0 8px 0",
                }}>
                  No immediate charge
                </p>
                <p style={{
                  fontSize: "36px",
                  fontWeight: 700,
                  color: "#64748b",
                  margin: "0 0 12px 0",
                  letterSpacing: "-1px",
                }}>
                  $0.00
                </p>
                <div style={{
                  height: "1px",
                  background: "#e2e8f0",
                  margin: "0 0 12px 0",
                }} />
                <p style={{
                  fontSize: "13px",
                  color: "#94a3b8",
                  margin: 0,
                }}>
                  Your billing will update on the next cycle
                </p>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div style={{
            display: "flex",
            gap: "12px",
          }}>
            <button
              onClick={() => setPreviewData(null)}
              style={{
                flex: 1,
                height: "48px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                color: "#475569",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => executeSubscriptionUpdate(null, previewData?.action)}
              disabled={loading}
              style={{
                flex: 1,
                height: "48px",
                borderRadius: "12px",
                border: "none",
                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {loading ? (
                "Processing..."
              ) : isCheckoutFlow ? (
                <>
                  <FiExternalLink size={16} />
                  Proceed to Checkout
                </>
              ) : (
                "Confirm"
              )}
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <>
      <Row
        gutter={[0, 24]}
        justify="center"
        style={{
          marginTop: "24px",
          padding: "0 12px",
        }}
      >
        {listMap}
      </Row>
      {renderPreviewModal()}
    </>
  );
};

export default PricingItem;
